import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, takeUntil, take } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { CustomListDataSourceService } from '../../shared/components/shared-list/custom-list-data-source.service';
import { ColumnSettingsItem } from '../../shared/components/shared-list/column-settings/column-settings.component';
import { FilterSettingsItem } from '../../shared/components/shared-list/filter-settings/filter-settings.component';
import { BackendFilteringObject, GridConfig } from '../models/grid-config.interface';
import { DynamicFiltersComponent } from '../../shared/components/dynamic-filters/dynamic-filters.component';
import { FilterConfig, FilterData } from '../models/filter-config.interface';

@Injectable()
export abstract class GenericDatasource<T> extends CustomListDataSourceService<T> {
  public filterSubjects: { [key: string]: BehaviorSubject<string> } = {};
  public filters$ = new BehaviorSubject<any>(null);
  
  protected filterSettings: FilterSettingsItem[] = [];
  protected visibleFiltersConfig: FilterConfig[] = [];
  protected filtersConfig: FilterConfig[] = [];
  protected FILTER_SETTINGS_STORAGE_KEY = '';

  protected config: GridConfig<T>;
  public currentSort: any = null;
  protected isInitialized = false;
  private filtersInitialized = false;

  /**
   * Reference to the dynamic filters component, if present. Allows global UI sync after CRUD.
   */
  public dynamicFiltersComponent?: DynamicFiltersComponent;

  /**
   * Call this in your grid component's ngAfterViewInit, passing the @ViewChild ref.
   */
  public setDynamicFiltersComponent(ref: DynamicFiltersComponent) {
    this.dynamicFiltersComponent = ref;
  }

  protected initialize(config: GridConfig<T>): void {
    this.config = config;
    
    // Create filter subjects dynamically
    Object.keys(config.filterFields).forEach(key => {
      this.filterSubjects[key] = new BehaviorSubject<string>('');
    });

    // Apply initial filters if provided, otherwise load from localStorage
    if (config.initialFilters && Object.keys(config.initialFilters).length > 0) {
      Object.keys(config.initialFilters).forEach(key => {
        if (this.filterSubjects[key] && config.initialFilters[key]) {
          this.filterSubjects[key].next(config.initialFilters[key]);
        }
      });
    } else {
      // No initial filters provided, load from localStorage
      this.loadFilterState();
    }

    this._loading$.next(true);
    this.loadSortState();

    this.loadPageState();
    this._pageChanged$.pipe(takeUntil(this._destroy$)).subscribe(state => {
      try {
        this.savePageState();
      } catch (e) {
      }
    });

    this.setupFilterSubscription(config.autoLoad !== false);
  }
  

  private loadSortState(): void {
    const savedSort = localStorage.getItem(this.config.storageKeys.sort);
    if (savedSort) {
      try {
        this.currentSort = JSON.parse(savedSort);
      } catch (e) {
        this.currentSort = null;
      }
    }
  }

  private setupFilterSubscription(autoLoad: boolean = true): void {
    const filterObservables = Object.keys(this.filterSubjects).map(key => 
      this.filterSubjects[key].pipe(startWith(this.filterSubjects[key].value))
    );

    combineLatest(filterObservables).pipe(
      debounceTime(300),
      distinctUntilChanged(),
      map((values) => {
        const filters: any = {};
        Object.keys(this.filterSubjects).forEach((key, index) => {
          filters[key] = values[index] || null;
        });
        return filters;
      }),
      takeUntil(this._destroy$)
    ).subscribe((value) => {
      this.filters$.next(value);
      this.saveFilterState();

      try {
        const pageState = (this as any)._pageChanged$?.getValue?.() ?? (this as any)._pageChanged$?.value;
        const currentLimit = (pageState && pageState.limit) ? pageState.limit : 10;

        if (this.filtersInitialized) {
          if ((this as any)._pageChanged$ && typeof (this as any)._pageChanged$.next === 'function') {
            (this as any)._pageChanged$.next({ page: 1, limit: currentLimit });
          }
        } else {
          this.filtersInitialized = true;
        }
      } catch (e) {
        console.warn('GenericDatasource: failed to reset page on filter change', e);
      }
    });

    if (autoLoad) {
      this.setupCombinedSubscription();
    }
  }

  public startDataLoading(): void {
    if (!this.isInitialized) {
      this.setupCombinedSubscription();
    }
  }

  private setupCombinedSubscription(): void {
    combineLatest([
      this._pageChanged$,
      this.filters$,
      this.refresh$
    ]).pipe(
      debounceTime(300),
      switchMap(([pagination, filters]) => {
        if (!this.isInitialized) {
          this.isInitialized = true;
        }
        this._loading$.next(true);
        
        const filteringObj = new BackendFilteringObject();
        
        if (pagination) {
          filteringObj.paginator.current_page = pagination.page || 1;
          filteringObj.paginator.per_page = pagination.limit || 10;
        }
        
        // Build filtering items based on config
        const filteringItems = [];
        Object.keys(this.config.filterFields).forEach(key => {
          const fieldConfig = this.config.filterFields[key];
          const value = filters ? filters[key] : null;
          
          // For size filter, we need to handle both empty and non-empty values
          if (value !== null && value !== undefined) {
            const trimmedValue = String(value).trim();
            if (trimmedValue !== '') {
              filteringItems.push({ 
                field: fieldConfig.backendField, 
                search: trimmedValue 
              });
            }
          }
        });
        
        // Ensure API call is made even if filteringItems is empty
        filteringObj.setFilteringItems(filteringItems);
        
        if (this.currentSort) {
          filteringObj.setSortingItems([{
            field: this.currentSort.active,
            direction: this.currentSort.direction
          }]);
        }
        return from(this.config.apiService.getData(filteringObj));
      }),
      takeUntil(this._destroy$)
    ).subscribe((res) => {
      // Debugging log to verify data handling

      if (res.data?.length > 0) {
        this._data$.next(res.data);
      } else {
        this._data$.next([]);
      }

      this.totalItems = res.paginationData?.totalItems || 0;
      this._loading$.next(false);

      // Reset page if current page is invalid
      const pageState = (this as any)._pageChanged$?.getValue?.();
      if (pageState && pageState.page > 1) {
        if (this.totalItems === 0) {
          (this as any)._pageChanged$.next({ page: 1, limit: pageState.limit });
        } else {
          const totalPages = Math.ceil(this.totalItems / (pageState.limit || 10));
          if (pageState.page > totalPages) {
            (this as any)._pageChanged$.next({ page: totalPages, limit: pageState.limit });
          }
        }
      }
    }, (error) => {
      this._data$.next([]);
      this.totalItems = 0;
      this._loading$.next(false);
    });
  }

  private saveFilterState(): void {
    const currentFilters = this.filters$.value;
    if (currentFilters) {
      const filtersToSave: any = {};
      Object.keys(this.config.filterFields).forEach(key => {
        const fieldConfig = this.config.filterFields[key];
        if (fieldConfig.persistent && currentFilters[key]) {
          filtersToSave[key] = currentFilters[key];
        }
      });
      localStorage.setItem(this.config.storageKeys.filters, JSON.stringify(filtersToSave));
    }
  }

  private loadFilterState(): void {
    const savedFilters = localStorage.getItem(this.config.storageKeys.filters);

    if (savedFilters) {

      try {
        const parsedFilters = JSON.parse(savedFilters);

        Object.keys(parsedFilters).forEach(key => {
          if (this.filterSubjects[key] && parsedFilters[key]) {
            this.filterSubjects[key].next(parsedFilters[key]);
          }
        });
      } catch (e) {
        // Invalid JSON, ignore
        console.error('Error parsing saved filters:', e);
      }
    }
  }

  public setFilter(filterName: string, value: string): void {
    if (this.filterSubjects[filterName]) {
      this.filterSubjects[filterName].next(value || '');
    }
  }

  public getFilter(filterName: string): BehaviorSubject<string> {
    return this.filterSubjects[filterName];
  }

  public clearFilter(filterName: string): void {
    if (this.filterSubjects[filterName]) {
      this.filterSubjects[filterName].next('');
    }
  }

  public clearAllFilters(): void {
    this._loading$.next(true);
    Object.keys(this.filterSubjects).forEach(filterName => {
      this.filterSubjects[filterName].next('');
    });
    
    if (this.config.storageKeys?.filters) {
      localStorage.removeItem(this.config.storageKeys.filters);
    }
    
    if (this.config.routerDependencies) {
      const { router, activatedRoute } = this.config.routerDependencies;
      router.navigate([], {
        relativeTo: activatedRoute,
        queryParams: {},
        replaceUrl: true
      });
    }
  }

  public loadLocalStorageFilters(): void {
    this.loadFilterState();
  }

  /**
   * Gets current filter values from all filter subjects
   * @param filterKeys Optional array of specific filter keys to get. If not provided, gets all filters.
   * @returns Object with filter keys and their current values
   */
  public getCurrentFilterValues(filterKeys?: string[]): { [key: string]: string } {
    const currentValues: { [key: string]: string } = {};
    const keysToProcess = filterKeys || Object.keys(this.filterSubjects);
    
    keysToProcess.forEach(key => {
      if (this.filterSubjects[key]) {
        currentValues[key] = this.filterSubjects[key].value || '';
      } else {
        console.warn(`Filter subject for key '${key}' not found`);
        currentValues[key] = '';
      }
    });
    
    return currentValues;
  }

  public getCurrentSort(): any {
    return this.currentSort;
  }

  /**
   * Returns the current page number from the internal pagination state.
   * Falls back to 1 when unavailable.
   */
  public getCurrentPage(): number {
    try {
      const pageState = (this as any)._pageChanged$?.getValue?.();
      return pageState && pageState.page ? pageState.page : 1;
    } catch (e) {
      return 1;
    }
  }

  public sortChanged(sort: any): void {
    if (!sort.active || sort.direction === '') {
      return;
    }

    this.currentSort = sort;
    localStorage.setItem(this.config.storageKeys.sort, JSON.stringify(sort));
    this.refresh$.next(null);
  }

  public onRefresh() {
    this.refreshData();
  }

  public refreshData(): void {
    this._loading$.next(true);
    this.refresh$.next(null);
  }

  /**
   * Updates filters based on new item data
   * Tracked filters are updated with new values, non-tracked filters are reset
   * @param oldItem The item before update (kept for backward compatibility)
   * @param newItem The item after update
   */
  public refreshDataAndUpdateFilters(
    newItem: T
  ): void {
    this._loading$.next(true);
    const currentFilters = this.filters$.value;
    const updatedValues: { [key: string]: any } = {};
    if (currentFilters && this.config?.filterFields) {
      const getNestedValue = (obj: any, path: string) => {
        return path.split('.').reduce((prev, curr) => 
          prev ? prev[curr] : undefined, obj);
      };
      const newItemAny = newItem as any;
      Object.keys(this.config.filterFields || {}).forEach(key => {
        const fieldConfig = this.config.filterFields[key];
        if (fieldConfig) {
          if (fieldConfig.track) {
            const propertyPath = fieldConfig.propertyPath || key;
            let newValue;
            if (propertyPath.endsWith('Id')) {
              newValue = newItemAny[propertyPath];
            } else {
              newValue = getNestedValue(newItemAny, propertyPath);
              if (typeof newValue === 'object' && newValue && newValue.id !== undefined) {
                newValue = newValue.id;
              }
            }
            if (newValue !== undefined) {
              this.filterSubjects[key].next(String(newValue));
              updatedValues[key] = String(newValue);
            }
          } else {
            this.filterSubjects[key].next('');
            updatedValues[key] = '';
          }
        }
      });
    }
    // Also update the dynamic filters form controls if present
    if (this.dynamicFiltersComponent && this.dynamicFiltersComponent.form) {
      Object.keys(updatedValues).forEach(key => {
        if (this.dynamicFiltersComponent.form.controls[key]) {
          this.dynamicFiltersComponent.form.get(key)?.setValue(updatedValues[key], { emitEvent: false });
        }
      });
    }
    // Trigger refresh
    this.refresh$.next(null);
  }

  /**
   * Refreshes data while clearing all filters
   */
  public refreshDataAndClearFilters(): void {
    this._loading$.next(true);
    // Clear all filters
    Object.keys(this.filterSubjects).forEach(key => {
      this.filterSubjects[key].next('');
    });
    this.refresh$.next(null);
  }

  public exportAllData(buttonsConfig: { exportAll?: { isLoading: boolean } }): void {
    if (!buttonsConfig.exportAll || buttonsConfig.exportAll.isLoading) {
      return;
    }

    const filteringObj = this.getCurrentFilters();

    buttonsConfig.exportAll.isLoading = true;
    this.config.apiService.exportData(filteringObj).then(() => {
      buttonsConfig.exportAll.isLoading = false;
    }).catch(err => {
      buttonsConfig.exportAll.isLoading = false;
      console.error('Export all failed:', err);
    });
  }

  public exportCurrentPageData(buttonsConfig: { export?: { isLoading: boolean } }): void {
    if (!buttonsConfig.export || buttonsConfig.export.isLoading) {
      return;
    }
    buttonsConfig.export.isLoading = true;
    this.connect(null).pipe(take(1)).subscribe(data => {
      if (data && data.length > 0) {
        const mutableData = [...data];
        this.config.apiService.exportPageData(mutableData)?.then(() => {
          buttonsConfig.export.isLoading = false;
        }).catch(err => {
          buttonsConfig.export.isLoading = false;
          console.error('Export Page Data Failed:', err);
        });
      } else {
        buttonsConfig.export.isLoading = false;
        console.warn('No data available to export.');
      }
    });
  }

  protected onSizeFilterChange(value: string): void {
    this.setFilter('size', value);
  }
  
  /**
   * Handles CRUD operations and updates filters with response data when successful
   * @param promise The promise returned by the CRUD operation
   * @param event The event object containing callbacks and data
   */
  public handleCrudAction(
    promise: Promise<any>,
    event: { confirm: { resolve: () => void; reject: () => void }, newData?: any, isDelete?: boolean }
  ): void {
    promise.then((response) => {
      event.confirm.resolve();
      // If response contains data, update filters and UI
      if (response && response.data) {
        this.refreshDataAndUpdateFilters(response.data);
      }
      // If no response data but we have newData from the event
      else if (event.newData) {
        this.refreshDataAndUpdateFilters(event.newData);
      }
      // For delete operations or when no data is returned, just refresh the data
      else {
        this.refreshData();
        // Also clear UI filters if present
        if (this.dynamicFiltersComponent && this.dynamicFiltersComponent.form) {
          Object.keys(this.dynamicFiltersComponent.form.controls).forEach(key => {
            this.dynamicFiltersComponent.form.get(key)?.setValue('', { emitEvent: false });
          });
        }
      }
    }).catch(err => {
      console.error('CRUD action failed:', err);
      event.confirm.reject();
    });
  }

  /**
   * Generic column settings initializer for grids.
   * @param columns The columns config array
   * @param storageKey The localStorage key for column settings
   * @param requiredKeys Array of column keys that should always be required
   * @returns ColumnSettingsItem[]
   */
  protected initializeColumnSettings(
    columns: { key: string; label: string }[],
    storageKey: string,
    requiredKeys: string[] = []
  ): ColumnSettingsItem[] {
    let columnSettings: ColumnSettingsItem[] = columns.map(column => ({
      key: column.key,
      label: column.label,
      visible: true,
      required: requiredKeys.includes(column.key)
    }));

    const savedSettings = localStorage.getItem(storageKey);
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        columnSettings.forEach(setting => {
          const savedSetting = parsedSettings.find((s: any) => s.key === setting.key);
          if (savedSetting !== undefined) {
            setting.visible = savedSetting.visible;
          }
        });
      } catch (e) {
        console.warn('Could not restore column settings:', e);
      }
    }
    return columnSettings;
  }

  /**
   * Initialize filter settings from localStorage
   * @param filters Array of filter objects with key and label
   * @param storageKey The localStorage key for filter settings
   * @returns FilterSettingsItem[]
   */
  protected initializeFilterSettings(
    filters: { key: string; label: string }[],
    storageKey: string
  ): FilterSettingsItem[] {
    let filterSettings: FilterSettingsItem[] = filters.map(filter => ({
      key: filter.key,
      label: filter.label,
      visible: true
    }));

    const savedSettings = localStorage.getItem(storageKey);
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        filterSettings.forEach(setting => {
          const savedSetting = parsedSettings.find((s: any) => s.key === setting.key);
          if (savedSetting !== undefined) {
            setting.visible = savedSetting.visible;
          }
        });
      } catch (e) {
        console.warn('Could not restore filter settings:', e);
      }
    }
    return filterSettings;
  }

  public getCurrentFilters(): BackendFilteringObject {
    const filteringObj = new BackendFilteringObject();
    const currentFilters = this.filters$.value;
    
    const filteringItems: Array<{ field: string; search: string }> = [];
    
    if (currentFilters && this.config?.filterFields) {
      Object.keys(this.config.filterFields).forEach(key => {
        const fieldConfig = this.config.filterFields[key];
        const value = currentFilters[key];
        
        if (value !== null && value !== undefined) {
          const trimmedValue = String(value).trim();
          if (trimmedValue !== '') {
            filteringItems.push({ 
              field: fieldConfig.backendField, 
              search: trimmedValue 
            });
          }
        }
      });
    }
    
    filteringObj.setFilteringItems(filteringItems);
    
    if (this.currentSort) {
      filteringObj.setSortingItems([{
        field: this.currentSort.active,
        direction: this.currentSort.direction
      }]);
    }
    
    return filteringObj;
  }

  public bindPaginator(paginator: MatPaginator): void {
    if (!paginator) return;

    try {
      const pageState = (this as any)._pageChanged$?.getValue?.() ?? (this as any)._pageChanged$?.value;
      if (pageState && typeof pageState.page === 'number') {
        paginator.pageIndex = Math.max(0, (pageState.page || 1) - 1);
        if (pageState.limit) {
          paginator.pageSize = pageState.limit;
        }
      }
    } catch (e) {
    }

    paginator.page.pipe(takeUntil(this._destroy$)).subscribe((ev) => {
      try {
        const currentState = (this as any)._pageChanged$?.getValue?.() ?? (this as any)._pageChanged$?.value;
        const evPage = (ev as any).pageIndex + 1;
        const evLimit = (ev as any).pageSize;
        if (currentState && currentState.page === evPage && currentState.limit === evLimit) {
          return;
        }
        this.pageChanged(ev);
      } catch (err) {
        if ((this as any)._pageChanged$ && typeof (this as any)._pageChanged$.next === 'function') {
          (this as any)._pageChanged$.next({ page: ev.pageIndex + 1, limit: ev.pageSize });
        }
      }
    });

    (this as any)._pageChanged$.pipe(takeUntil(this._destroy$)).subscribe((ps: any) => {
      if (!ps) return;
      const newIndex = Math.max(0, (ps.page || 1) - 1);
      if (paginator.pageIndex !== newIndex) {
        paginator.pageIndex = newIndex;
      }
      if (ps.limit && paginator.pageSize !== ps.limit) {
        paginator.pageSize = ps.limit;
      }
    });
  }

  private loadPageState(): void {
    const key = this.config?.storageKeys?.page || (this.config?.entityName ? `${this.config.entityName}_page` : null);
    if (!key) return;

    try {
      const parsed = this.safeLocalStorageGet(key);
      if (parsed && typeof parsed.page === 'number') {
        if ((this as any)._pageChanged$ && typeof (this as any)._pageChanged$.next === 'function') {
          (this as any)._pageChanged$.next({ page: parsed.page, limit: parsed.limit });
        }
      }
    } catch (e) {
      console.warn('GenericDatasource: failed to restore saved page state', e);
    }
  }

  private savePageState(): void {
    const key = this.config?.storageKeys?.page || (this.config?.entityName ? `${this.config.entityName}_page` : null);
    if (!key) return;

    try {
      const pageState = (this as any)._pageChanged$?.getValue?.() ?? (this as any)._pageChanged$?.value;
      if (pageState && typeof pageState.page === 'number') {
        const toSave = { page: pageState.page, limit: pageState.limit };
        this.safeLocalStorageSet(key, toSave);
      }
    } catch (e) {
      console.warn('GenericDatasource: failed to save page state', e);
    }
  }

  protected updateVisibleFiltersConfig(): void {
    if (!this.filtersConfig || !this.filterSettings) {
      console.warn('updateVisibleFiltersConfig: filtersConfig or filterSettings not initialized');
      return;
    }
    
    this.visibleFiltersConfig = this.filtersConfig.filter(filter => {
      const setting = this.filterSettings.find(s => s.key === filter.key);
      return setting ? setting.visible : true;
    });
  }

  protected updateFilterActiveStatusFromData(filterData: FilterData): void {
    if (!this.filterSettings) {
      console.warn('updateFilterActiveStatusFromData: filterSettings not initialized');
      return;
    }

    this.filterSettings = this.filterSettings.map(setting => ({
      ...setting,
      active: (() => {
        const filterValue = filterData[setting.key];
        return filterValue !== null && filterValue !== undefined && String(filterValue).trim() !== '';
      })()
    }));
  }

  protected updateFilterActiveStatusFromCurrentValues(): void {
    if (!this.filterSubjects) {
      console.warn('updateFilterActiveStatusFromCurrentValues: filterSubjects not initialized');
      return;
    }

    const currentFilters: FilterData = {};
    Object.keys(this.filterSubjects).forEach(key => {
      currentFilters[key] = this.filterSubjects[key].value;
    });
    this.updateFilterActiveStatusFromData(currentFilters);
  }
}
